import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Trash2, Star, Save, Phone, Mail, MapPin, Info, ImagePlus, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useSiteSettings, apiImg } from "@/lib/siteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-lg border border-line bg-warm/60 px-3.5 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition";
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft mb-2";

const Card = ({ icon: Icon, title, hint, children, testid }) => (
  <div className="bg-white rounded-xl border border-line p-6 sm:p-8" data-testid={testid}>
    <div className="flex items-center gap-3 mb-6">
      <span className="h-10 w-10 rounded-lg bg-navy flex items-center justify-center shrink-0">
        <Icon size={16} className="text-gold" />
      </span>
      <div>
        <h3 className="font-display text-lg sm:text-xl font-semibold text-navy">{title}</h3>
        {hint && <p className="text-[12px] text-ink-soft">{hint}</p>}
      </div>
    </div>
    {children}
  </div>
);

export default function ContentTab() {
  const { site, refresh } = useSiteSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [props_, setProps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState("");

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("rk_admin_token")}` });

  useEffect(() => {
    if (site) {
      setForm({
        email: site.email || "", whatsappNumber: site.whatsappNumber || "",
        contactHeading: site.contactHeading || "", contactDesc: site.contactDesc || "",
        phones: site.phones || [], address: site.address || "",
        aboutHeading: site.aboutHeading || "", aboutDesc: site.aboutDesc || "",
        aboutDesc2: site.aboutDesc2 || "", aboutImage: site.aboutImage || "",
      });
    }
  }, [site]);

  useEffect(() => {
    axios.get(`${API}/admin/properties`, { headers: headers() }).then(({ data }) => setProps(data)).catch(() => {});
    axios.get(`${API}/admin/projects`, { headers: headers() }).then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, form, { headers: headers() });
      await refresh();
      toast.success("Website content saved — live on the site now.");
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const addPhone = () => setForm((f) => ({ ...f, phones: [...f.phones, { id: `p-${Date.now()}`, number: "" }] }));
  const setPhone = (id, val) =>
    setForm((f) => ({ ...f, phones: f.phones.map((p) => (p.id === id ? { ...p, number: val } : p)) }));
  const removePhone = (id) => setForm((f) => ({ ...f, phones: f.phones.filter((p) => p.id !== id) }));

  const uploadAboutImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/uploads`, fd, { headers: headers() });
      setForm((f) => ({ ...f, aboutImage: data.path }));
      toast.success("Image uploaded — remember to Save Changes.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const toggleFeatured = async (kind, item) => {
    setBusy(item.id);
    try {
      await axios.put(`${API}/admin/${kind}/${item.id}`, { ...item, featured: !item.featured }, { headers: headers() });
      const setter = kind === "properties" ? setProps : setProjects;
      setter((list) => list.map((x) => (x.id === item.id ? { ...x, featured: !x.featured } : x)));
      toast.success(item.featured ? "Removed from homepage." : "Now featured on the homepage.");
    } catch {
      toast.error("Could not update. Please try again.");
    } finally {
      setBusy("");
    }
  };

  if (!form) return <div className="py-16 flex justify-center text-ink-soft"><Loader2 className="animate-spin" size={26} /></div>;

  const FeatureRow = ({ kind, item, label, sub }) => (
    <div className="flex items-center justify-between gap-3 border border-line rounded-lg px-4 py-3" data-testid={`featured-row-${item.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-navy truncate">{label}</p>
        <p className="text-[12px] text-ink-soft truncate">{sub}</p>
      </div>
      <button
        onClick={() => toggleFeatured(kind, item)}
        disabled={busy === item.id}
        data-testid={`featured-toggle-${item.id}`}
        className={`shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold rounded-md px-3 py-2 border transition-colors disabled:opacity-50 ${
          item.featured ? "bg-gold/20 border-gold/50 text-navy" : "border-line text-ink-soft hover:border-gold hover:text-navy"
        }`}
      >
        <Star size={12} className={item.featured ? "fill-gold text-gold" : ""} />
        {item.featured ? "Featured" : "Feature"}
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="content-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-navy">Website Content</h2>
          <p className="mt-1 text-sm text-ink-soft">Saved changes appear on the public website immediately.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button"
          className="bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-6 py-3 text-[14px] font-semibold inline-flex items-center gap-2 shadow-md shadow-gold/25 disabled:opacity-50"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
        </button>
      </div>

      <Card icon={Mail} title="Contact Details" hint="Shown in the contact section and footer." testid="content-contact-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Email Address</label>
            <input className={inputCls} placeholder="you@rkrealestate.com" value={form.email} onChange={set("email")} data-testid="content-email" />
          </div>
          <div>
            <label className={labelCls}>WhatsApp Number</label>
            <input className={inputCls} placeholder="919829710557" value={form.whatsappNumber} onChange={set("whatsappNumber")} data-testid="content-whatsapp" />
            <p className="mt-1.5 text-[11px] text-ink-soft">Country code + number, digits only.</p>
          </div>
        </div>
        <div className="mt-5">
          <label className={labelCls}>Contact Section Heading</label>
          <input className={inputCls} placeholder="Tell Us What You're Looking For." value={form.contactHeading} onChange={set("contactHeading")} data-testid="content-contact-heading" />
        </div>
        <div className="mt-5">
          <label className={labelCls}>Contact Section Description</label>
          <textarea rows={2} className={`${inputCls} h-auto py-3 resize-none`} placeholder="Share your requirement and our team can help..." value={form.contactDesc} onChange={set("contactDesc")} data-testid="content-contact-desc" />
        </div>
      </Card>

      <Card icon={Phone} title="Phone Numbers" hint="Every number becomes tap-to-call on the website." testid="content-phones-card">
        <div className="space-y-3" data-testid="content-phones-list">
          {form.phones.map((p) => (
            <div key={p.id} className="flex gap-2.5">
              <input className={inputCls} placeholder="+91 98XXX XXXXX" value={p.number} onChange={(e) => setPhone(p.id, e.target.value)} data-testid={`content-phone-${p.id}`} />
              <button onClick={() => removePhone(p.id)} className="shrink-0 h-12 w-12 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center" aria-label="Delete number" data-testid={`content-phone-delete-${p.id}`}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {form.phones.length === 0 && <p className="text-sm text-ink-soft">No numbers added — the website shows its default numbers until you add some.</p>}
        </div>
        <button onClick={addPhone} className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-navy border border-line hover:border-gold rounded-lg px-4 py-2.5 transition-colors" data-testid="content-add-phone">
          <Plus size={14} /> Add Phone Number
        </button>
      </Card>

      <Card icon={MapPin} title="Office Address" hint="Shown in the footer contact column." testid="content-address-card">
        <textarea rows={2} className={`${inputCls} h-auto py-3 resize-none`} placeholder="Office address" value={form.address} onChange={set("address")} data-testid="content-address" />
      </Card>

      <Card icon={Info} title="About Us Content" hint="Shown in the About section on the homepage." testid="content-about-card">
        <label className={labelCls}>About Us Heading</label>
        <input className={inputCls} placeholder="Real Estate, Made Simpler." value={form.aboutHeading} onChange={set("aboutHeading")} data-testid="content-about-heading" />
        <div className="mt-5">
          <label className={labelCls}>About Us Description</label>
          <textarea rows={3} className={`${inputCls} h-auto py-3 resize-none`} placeholder="First paragraph..." value={form.aboutDesc} onChange={set("aboutDesc")} data-testid="content-about-desc" />
        </div>
        <div className="mt-5">
          <label className={labelCls}>Second Paragraph</label>
          <textarea rows={3} className={`${inputCls} h-auto py-3 resize-none`} placeholder="Second paragraph..." value={form.aboutDesc2} onChange={set("aboutDesc2")} data-testid="content-about-desc2" />
        </div>
        <div className="mt-5">
          <label className={labelCls}>About Us Image</label>
          <div className="flex items-start gap-4">
            {form.aboutImage ? (
              <div className="relative w-48 aspect-[4/3] rounded-lg overflow-hidden border border-line">
                <img src={apiImg(form.aboutImage)} alt="About" className="w-full h-full object-cover" />
                <button onClick={() => setForm((f) => ({ ...f, aboutImage: "" }))} className="absolute top-2 right-2 h-7 w-7 rounded-md bg-navy/80 text-white flex items-center justify-center hover:bg-red-600 transition-colors" aria-label="Remove image" data-testid="content-about-image-remove">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="w-48 aspect-[4/3] rounded-lg border-2 border-dashed border-line hover:border-gold flex flex-col items-center justify-center gap-2 cursor-pointer text-ink-soft hover:text-navy transition-colors" data-testid="content-about-image-upload">
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                <span className="text-[11px] font-semibold">{uploading ? "Uploading..." : "Upload Image"}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadAboutImage} />
              </label>
            )}
          </div>
          <p className="mt-2 text-[11px] text-ink-soft">Leave empty to keep the current default image.</p>
        </div>
      </Card>

      <Card icon={Star} title="Featured Properties" hint="Featured + published properties appear on the homepage." testid="content-featured-props-card">
        {props_.length === 0 ? (
          <p className="text-sm text-ink-soft">No properties yet — add them in the Properties tab, then feature them here.</p>
        ) : (
          <div className="space-y-3">
            {props_.map((p) => <FeatureRow key={p.id} kind="properties" item={p} label={p.title} sub={`${p.location} · ${p.price}${p.published ? "" : " · DRAFT (publish to show)"}`} />)}
          </div>
        )}
      </Card>

      <Card icon={Star} title="Featured Projects" hint="Featured + published projects appear in the homepage Featured Projects section." testid="content-featured-projects-card">
        {projects.length === 0 ? (
          <p className="text-sm text-ink-soft">No projects yet — add them in the Projects tab, then feature them here.</p>
        ) : (
          <div className="space-y-3">
            {projects.map((p) => <FeatureRow key={p.id} kind="projects" item={p} label={p.name} sub={`${p.location} · ${p.status}${p.published ? "" : " · DRAFT (publish to show)"}`} />)}
          </div>
        )}
      </Card>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button-bottom"
          className="bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-8 py-3.5 text-[15px] font-semibold inline-flex items-center gap-2 shadow-md shadow-gold/25 disabled:opacity-50"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
        </button>
      </div>
    </div>
  );
}
